/**
 * Cognito session store (phase 2). Tokens live in sessionStorage. Full id_token
 * validation happens in phase 3; this module only base64url-decodes claims.
 */

import { STORAGE_KEYS } from './constants';

/** Decode a JWT payload without verifying the signature. */
export const decodeJwtPayload = (jwt) => {
  try {
    const part = (jwt || '').split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(pad);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
};

/** Persist the token response and derived id_token claims to sessionStorage. */
export const saveSession = (tokens = {}) => {
  const { id_token: idToken, access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn } = tokens;

  if (idToken) sessionStorage.setItem(STORAGE_KEYS.ID_TOKEN, idToken);
  if (accessToken) sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  if (refreshToken) sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  if (expiresIn) {
    sessionStorage.setItem(STORAGE_KEYS.EXPIRES_AT, String(Date.now() + Number(expiresIn) * 1000));
  }

  const claims = idToken ? decodeJwtPayload(idToken) : null;
  if (claims && claims.sub) {
    sessionStorage.setItem(STORAGE_KEYS.USER_SUB, claims.sub);
  }
  if (claims && claims.email) {
    sessionStorage.setItem(STORAGE_KEYS.USER_EMAIL, claims.email);
  }
  return claims;
};

/** @returns {string|null} stored access_token */
export const getStoredAccessToken = () => sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

/** @returns {string|null} stored id_token */
export const getStoredIdToken = () => sessionStorage.getItem(STORAGE_KEYS.ID_TOKEN);

/** @returns {string|null} stored refresh_token */
export const getStoredRefreshToken = () => sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

/** @returns {number} absolute access_token expiry in ms, or 0 when unknown */
export const getSessionExpiry = () => Number(sessionStorage.getItem(STORAGE_KEYS.EXPIRES_AT)) || 0;

/** @returns {string|null} stable user identifier (id_token sub) */
export const getUserSub = () => sessionStorage.getItem(STORAGE_KEYS.USER_SUB);

/** @returns {string|null} Cognito user uuid / cuid (id_token sub) */
export const getUserCuid = () => sessionStorage.getItem(STORAGE_KEYS.USER_SUB);

/** @returns {string|null} logged-in user's email (id_token email claim) */
export const getUserEmail = () => sessionStorage.getItem(STORAGE_KEYS.USER_EMAIL);

/** @returns {boolean} whether an unexpired access_token exists */
export const isSessionActive = () => {
  const token = getStoredAccessToken();
  const expiresAt = Number(sessionStorage.getItem(STORAGE_KEYS.EXPIRES_AT));
  return Boolean(token) && (!expiresAt || Date.now() < expiresAt);
};

/** Remove all Cognito session tokens. */
export const clearCognitoSession = () => {
  sessionStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
  sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  sessionStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
  sessionStorage.removeItem(STORAGE_KEYS.USER_SUB);
  sessionStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
};
